// First entry wins, matching lodash uniqBy — the order the records arrive in
// decides which frame stands in for a record that several frames point at.
function uniqById(records) {
  const seen = new Set();
  return records.filter(({ record }) => {
    if (seen.has(record.id)) return false;
    seen.add(record.id);
    return true;
  });
}

// Which group map holds the records a location points at. Mirrors locRecord in
// #src/extensions/filter-items.js, which resolves the record itself.
function groupMapKey(recordType) {
  if (recordType === "type") return "types";
  if (recordType === "document_note") return "document_notes";
  return "items";
}

// Marks are numbered per namespace, not per group. $group.items holds items
// AND collections, and the app mints their marks from two separate sequences
// (see getNextMark in Document.svelte), so an item and a collection sharing a
// mark is legal and must not be reported as a collision.
function namespaceOf(record, recordType) {
  const key = groupMapKey(recordType);
  if (key !== "items") return key;
  return record?.is_collection ? "collections" : "plainItems";
}

function recordsInNamespace(grp, namespace) {
  if (namespace === "types") return Object.values(grp?.types || {});
  if (namespace === "document_notes")
    return Object.values(grp?.document_notes || {});

  const items = Object.values(grp?.items || {});
  const wantCollection = namespace === "collections";
  return items.filter(
    (record) => Boolean(record?.is_collection) === wantCollection,
  );
}

/**
 * The records a frame selection should update, in the order the new marks are
 * applied: by page, then top to bottom, then left to right.
 *
 * Reversing happens before de-duplication, as it always has — when two
 * selected frames point at one record, the order decides which frame stands in
 * for it, and that in turn decides the mark the record ends up with.
 *
 * @param {object[]} locations selected locations
 * @param {Record<string, number>} visibleIndices page id to display index
 * @param {object} grp the group store value
 * @param {(location: object, grp: object) => object | null} resolve locRecord
 * @param {boolean} [reverse] reverse label order
 * @returns {{ record: object, type: string }[]}
 */
export function orderFrameRecords(
  locations,
  visibleIndices,
  grp,
  resolve,
  reverse = false,
) {
  // Page indices are derived reactively upstream, so tolerate them not being
  // ready yet rather than throwing mid-sort.
  const indices = visibleIndices || {};

  const sorted = (locations || []).slice().sort((a, b) => {
    if (a.page_id === b.page_id) {
      return a.position.y === b.position.y
        ? a.position.x - b.position.x
        : a.position.y - b.position.y;
    }
    return indices[a.page_id] - indices[b.page_id];
  });

  if (reverse) sorted.reverse();

  const records = sorted
    .map((loc) => ({ record: resolve(loc, grp), type: loc.record_type }))
    .filter((r) => r.record);

  return uniqById(records);
}

/**
 * Marks already in use by records that share a namespace with the selection
 * but are not part of this update. Feeds the duplicate warning.
 *
 * @param {{ record: object, type: string }[]} selection
 * @param {object} grp the group store value
 * @returns {string[]}
 */
export function otherMarksInScope(selection, grp) {
  const list = selection || [];
  const updating = new Set(list.map((r) => r.record.id));
  const namespaces = new Set(list.map((r) => namespaceOf(r.record, r.type)));
  const marks = [];

  namespaces.forEach((namespace) => {
    recordsInNamespace(grp, namespace).forEach((record) => {
      if (record?.mark && !updating.has(record.id)) marks.push(record.mark);
    });
  });

  return marks;
}

/**
 * The mark the renumber field should start from.
 *
 * Taken from the first record in READING order, never the reversed order.
 * Reverse Label Order is meant to hand the same range of marks out back to
 * front; seeding the sequence from the reversed first record instead walks the
 * range upward every time it is used.
 *
 * @param {object[]} locations selected locations
 * @param {Record<string, number>} visibleIndices page id to display index
 * @param {object} grp the group store value
 * @param {(location: object, grp: object) => object | null} resolve locRecord
 * @param {string} [fallback]
 * @returns {string}
 */
export function startMarkFor(
  locations,
  visibleIndices,
  grp,
  resolve,
  fallback = "A1",
) {
  const forward = orderFrameRecords(
    locations,
    visibleIndices,
    grp,
    resolve,
    false,
  );
  return forward[0]?.record?.mark || fallback;
}

/**
 * The group.update payload for an applied mark update, carrying ONLY the
 * records whose mark actually changes.
 *
 * Renumber used to change every selected mark by definition, so writing them
 * all was free. Add text and find-and-replace routinely leave most of a
 * selection alone, and writing those anyway bumps updated_at on untouched
 * records, pads the undo transaction, and re-sends a mark read when the dialog
 * opened — clobbering any edit a peer made to it since.
 *
 * @param {{ record: object, type: string }[]} selection
 * @param {string[]} newMarks positionally matched to `selection`
 * @returns {{ type: string, id: string, prop: string, value: string }[]}
 */
export function changedUpdates(selection, newMarks) {
  const marks = newMarks || [];

  return (selection || []).reduce((updates, { record, type }, i) => {
    const value = marks[i];
    if (value === undefined || value === record.mark) return updates;
    updates.push({ type, id: record.id, prop: "mark", value });
    return updates;
  }, []);
}
